import { keyframes } from '@emotion/react';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import styled from '@emotion/styled';
import { subscriptionStore } from '@/stores/SubscriptionStore';
import { paywallFlowStore } from '@/stores/PaywallFlowStore';
import { isApplePayCancelledError, useTrialPayment } from '@/hooks/useTrialPayment';
import { sendLoadSuccess, sendPaymentFailed } from '@/hooks/useWebViewMessaging';
import { PaymentError } from '@/components/ui/PaymentError';
import { IosSettingsIcon, MiniIphoneIcon } from '@/components/ui/IosReferenceIcons';
import { syncPaywallMetaFromUrl } from '@/utils/paywallUrlParams';
import batteryIconUrl from '@/assets/ios-app-icons/battery.png?url';
import cellularIconUrl from '@/assets/ios-app-icons/cellular.png?url';
import contactsIconUrl from '@/assets/ios-app-icons/contacts.png?url';
import photosIconUrl from '@/assets/ios-app-icons/photos.png?url';

const IOS_SYSTEM_GRAY_5_LIGHT = '#e5e5ea';
const IOS_SYSTEM_BLUE = '#007aff';
const IOS_SYSTEM_RED = '#ff3b30';
const IOS_SYSTEM_YELLOW = '#ffcc00';
const IOS_LABEL_LIGHT = '#000000';
const IOS_LABEL_DARK = '#ffffff';
const IOS_SECONDARY_LABEL_LIGHT = 'rgba(60, 60, 67, 0.60)';
const IOS_STRONG_SECONDARY_LABEL_LIGHT = 'rgba(60, 60, 67, 0.72)';
const IOS_QUATERNARY_LABEL_LIGHT = 'rgba(60, 60, 67, 0.18)';
const IOS_FONT_TEXT = '-apple-system, BlinkMacSystemFont, \'SF Pro Text\', \'SF Pro Display\', \'Helvetica Neue\', Arial, sans-serif';
const IOS_FONT_DISPLAY = '-apple-system, BlinkMacSystemFont, \'SF Pro Display\', \'SF Pro Text\', \'Helvetica Neue\', Arial, sans-serif';
const STAGE_TRANSITION_MS = 360;
const ISSUES_PROGRESS_DURATION_MS = 3600;
const ISSUES_COMPLETION_HOLD_MS = 1000;
const ISSUE_ROW_SCROLL_ANIMATION_MS = 36;
const CONFIRM_DIALOG_EXIT_MS = 180;
const NOTIFICATION_ENTER_DELAY_MS = 620;
const NOTIFICATION_TRAVEL_MS = 540;
const NOTIFICATION_EXIT_TRAVEL_MS = 500;
const NOTIFICATION_UNMOUNT_BUFFER_MS = 80;
const APPLE_PAY_UNAVAILABLE_ERROR = 'Apple Pay is not available on this device. Please use Safari on iOS or macOS.';

type PaywallStage = 'intro' | 'issues' | 'malware';

const ISSUE_RISK_SEQUENCE = [
  'High',
  'High',
  'Critical',
  'High',
  'High',
  'Critical',
  'High',
  'High',
] as const;

const ISSUE_VULNERABILITIES = [
  { code: 'MC-101', message: 'Suspicious configuration profile installed' },
  { code: 'MDM-1201', message: 'Unknown mobile device management profile' },
  { code: 'SEC-67843', message: 'Untrusted certificate authority enabled' },
  { code: 'WK-102', message: 'Safari redirect chain hijacks browsing session' },
  { code: 'CAL-403', message: 'Calendar subscription spam permission granted' },
  { code: 'UIPB-120', message: 'Clipboard access observed in background' },
  { code: 'CL-0001', message: 'Location updates exposed to unknown service' },
  { code: 'CN-100', message: 'Contacts sync attempted by unverified app' },
  { code: 'PH-3302', message: 'Photos library permission opened unexpectedly' },
  { code: 'AV-11852', message: 'Camera permission requested outside active session' },
  { code: 'AUD-11800', message: 'Microphone permission requested outside active session' },
  { code: 'BT-200', message: 'Bluetooth scan permission exposed' },
  { code: 'NW-0057', message: 'Local network discovery enabled for unknown app' },
  { code: 'APNS-3010', message: 'Push notification token leaked to remote host' },
  { code: 'CK-0009', message: 'iCloud backup access request detected' },
  { code: 'KEY-25308', message: 'Keychain item read by unknown process' },
  { code: 'LA-0004', message: 'Face ID policy check bypass attempt' },
  { code: 'PASS-1009', message: 'Passcode settings change attempt detected' },
  { code: 'RPK-5802', message: 'Screen recording permission probe detected' },
  { code: 'WKST-0018', message: 'WebKit storage injected tracking script' },
  { code: 'COOKIE-05', message: 'Safari session cookie marked insecure' },
  { code: 'NEVPN-13', message: 'Untrusted VPN configuration discovered' },
  { code: 'NEDNS-07', message: 'DNS proxy profile redirects secure traffic' },
  { code: 'MAIL-103', message: 'Mail account configuration modified' },
  { code: 'SMS-0002', message: 'SMS autofill code access attempted' },
  { code: 'PKPASS-12', message: 'Wallet pass metadata read attempt' },
  { code: 'HK-0004', message: 'Health data permission requested unexpectedly' },
  { code: 'FMIP-401', message: 'Find My location state queried remotely' },
  { code: 'ANL-0061', message: 'System analytics profile uploads device data' },
  { code: 'BG-0002', message: 'Background app refresh enabled suspiciously' },
  { code: 'ATT-0003', message: 'App tracking permission forced on' },
  { code: 'PRIV-008', message: 'Private relay availability check blocked' },
  { code: 'CFN-0306', message: 'Network proxy auto config modified' },
  { code: 'CAP-1003', message: 'Captive portal redirect loop detected' },
  { code: 'TLS-1202', message: 'Certificate pinning failure observed' },
  { code: 'WKB-0021', message: 'WebView bridge exposes native callback' },
  { code: 'URL-0400', message: 'Deep link handler accepts unsafe payload' },
  { code: 'AASA-1024', message: 'Universal link domain mismatch detected' },
  { code: 'PB-0002', message: 'Pasteboard token reuse detected' },
  { code: 'MCP-1007', message: 'Downloaded profile awaiting install' },
  { code: 'WCLIP-201', message: 'Home screen web clip with tracking URL' },
  { code: 'HIST-403', message: 'Browser history export attempt blocked' },
  { code: 'ASCR-1200', message: 'Password autofill domain mismatch' },
  { code: 'KEY-34018', message: 'Session token stored without protection' },
  { code: 'SSL-9807', message: 'Insecure TLS fallback attempt detected' },
  { code: 'JS-0701', message: 'Remote JavaScript injection attempt' },
  { code: 'APPG-513', message: 'Unknown app group container access' },
  { code: 'FILE-257', message: 'Temporary files contain personal data' },
  { code: 'CACHE-016', message: 'Cache contains unencrypted identifiers' },
  { code: 'SKAN-004', message: 'Ad attribution token replay detected' },
  { code: 'KBD-0110', message: 'Keyboard extension data access attempt' },
  { code: 'CTSIM-17', message: 'SIM toolkit status queried by unknown app' },
  { code: 'CTC-2002', message: 'Cellular carrier profile validation failed' },
  { code: 'WIFI-004', message: 'Wi-Fi network list access attempted' },
  { code: 'NBI-0002', message: 'Nearby devices scan request detected' },
  { code: 'DROP-501', message: 'AirDrop visibility state queried' },
  { code: 'UN-0003', message: 'Notification preview setting modified' },
  { code: 'WGK-0017', message: 'Lock screen widget data exposure' },
  { code: 'FOCUS-01', message: 'Focus mode state read by unknown app' },
  { code: 'ST-0005', message: 'Screen time settings probe detected' },
  { code: 'REST-409', message: 'Restrictions profile conflict detected' },
  { code: 'MI-0013', message: 'App install source cannot be verified' },
  { code: 'CL-0002', message: 'Background location flag left enabled' },
  { code: 'CM-0104', message: 'Motion sensor access in background' },
  { code: 'CNDB-002', message: 'Contacts database open handle detected' },
  { code: 'PHIMG-3300', message: 'Photo metadata export attempt blocked' },
  { code: 'VMEMO-09', message: 'Voice memo library access request' },
  { code: 'EKR-0017', message: 'Reminders database permission probe' },
  { code: 'NOTES-410', message: 'Notes attachment cache readable' },
  { code: 'EK-0403', message: 'Calendar events export attempt' },
  { code: 'SFEXT-100', message: 'Safari extension unsigned rule set' },
  { code: 'RTC-0701', message: 'WebRTC local IP exposure detected' },
  { code: 'ML-0012', message: 'Media library token reuse detected' },
  { code: 'PRIVM-09', message: 'App privacy manifest mismatch' },
  { code: 'UIDEV-01', message: 'Device name exposed in request header' },
  { code: 'AK-7003', message: 'Apple ID session validation warning' },
  { code: 'CKKS-045', message: 'iCloud keychain sync state queried' },
  { code: 'SEP-0101', message: 'Secure enclave attestation retry failed' },
  { code: 'XPRO-080', message: 'Malware signature matched in cache' },
  { code: 'REM-0001', message: 'Removal action required immediately' },
] as const;

const ISSUE_ROWS = ISSUE_VULNERABILITIES.map(({ code, message }, index) => ({
  code,
  risk: ISSUE_RISK_SEQUENCE[index % ISSUE_RISK_SEQUENCE.length],
  message,
}));
const TOTAL_DETECTED_ISSUES = ISSUE_ROWS.length;
const ISSUE_ROW_REVEAL_INTERVAL_MS = ISSUES_PROGRESS_DURATION_MS / TOTAL_DETECTED_ISSUES;

const stageEnterFromRight = keyframes`
  from {
    opacity: 0.98;
    transform: translate3d(100%, 0, 0);
  }
  to {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }
`;

const stageExitToLeft = keyframes`
  from {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }
  to {
    opacity: 0.92;
    transform: translate3d(-32%, 0, 0);
  }
`;

const stageFadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const stageFadeOut = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`;

const Page = styled.div`
  width: 100%;
  min-height: 100vh;
  min-height: 100svh;
  background: ${IOS_SYSTEM_GRAY_5_LIGHT};
`;

const Screen = styled.main`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 430px;
  min-height: 100svh;
  margin: 0 auto;
  padding:
    var(--app-safe-area-top)
    var(--app-safe-area-right)
    var(--app-safe-area-bottom)
    var(--app-safe-area-left);
  overflow: hidden;
  background: ${IOS_SYSTEM_GRAY_5_LIGHT};
  color: ${IOS_LABEL_LIGHT};
  color-scheme: light;
  font-family: ${IOS_FONT_TEXT};
`;

const StageViewport = styled.div`
  position: relative;
  flex: 1;
  width: 100%;
  min-height: 100svh;
  min-height: var(--app-safe-area-viewport-height);
  overflow: hidden;
  background: ${IOS_SYSTEM_GRAY_5_LIGHT};
`;

const StagePanel = styled.div<{ $phase: 'stable' | 'enter' | 'exit' }>`
  position: absolute;
  inset: 0;
  z-index: ${({ $phase }) => ($phase === 'exit' ? 1 : 2)};
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 100svh;
  min-height: var(--app-safe-area-viewport-height);
  background: ${IOS_SYSTEM_GRAY_5_LIGHT};
  pointer-events: ${({ $phase }) => ($phase === 'exit' ? 'none' : 'auto')};
  transform: translate3d(0, 0, 0);
  will-change: ${({ $phase }) => ($phase === 'stable' ? 'auto' : 'transform, opacity')};
  animation: ${({ $phase }) => {
    if ($phase === 'enter') return stageEnterFromRight;
    if ($phase === 'exit') return stageExitToLeft;
    return 'none';
  }} ${STAGE_TRANSITION_MS}ms cubic-bezier(0.32, 0.72, 0, 1) both;

  @media (prefers-reduced-motion: reduce) {
    animation: ${({ $phase }) => {
      if ($phase === 'enter') return stageFadeIn;
      if ($phase === 'exit') return stageFadeOut;
      return 'none';
    }} 120ms ease-out both;
  }
`;

const Title = styled.h1`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 52px;
  margin: 0;
  margin-bottom: 12px;
  color: ${IOS_LABEL_LIGHT};
  font-family: ${IOS_FONT_DISPLAY};
  text-align: center;
  font-size: 12px;
  line-height: 1;
  font-weight: 600;
`;

const DeviceWrap = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 19px;
`;

const Main = styled.section`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: stretch;
  padding: 0 26px 22px;
`;

const ScanBody = styled.section`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  padding: 0 30px 22px;
  color: ${IOS_LABEL_LIGHT};
`;

const SecurityIconWrap = styled.div`
  margin: 20px 0 12px;
`;

const IntroStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`;

const IntroSecurityCard = styled.div`
  overflow: hidden;
  width: 100%;
  border: 1px solid rgba(255, 255, 255, 0.38);
  border-radius: 18px;
  background: rgba(242, 242, 247, 0.48);
  box-shadow: 0 18px 42px rgba(0, 0, 0, 0.08);

  ${SecurityIconWrap} {
    margin-top: 18px;
  }
`;

const IntroSecurityMain = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 18px 18px;
`;

const AppleSecurityTitle = styled.h2`
  margin: 0 0 9px;
  color: ${IOS_LABEL_LIGHT};
  font-family: ${IOS_FONT_DISPLAY};
  text-align: center;
  font-size: 14px;
  line-height: 1.1;
  font-weight: 600;
`;

const IntroCopy = styled.p`
  margin: 0 auto;
  max-width: 230px;
  color: ${IOS_SECONDARY_LABEL_LIGHT};
  text-align: center;
  font-size: 10px;
  line-height: 1.35;
`;

const IntroCopyPanel = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  padding: 0 16px;
  border-top: 1px solid ${IOS_QUATERNARY_LABEL_LIGHT};
  background: rgba(255, 255, 255, 0.28);
`;

const ScanDivider = styled.div`
  width: 128px;
  height: 2px;
  margin: 35px 0 18px;
  border-radius: 99px;
  background: ${IOS_LABEL_LIGHT};
`;

const ScanDividerAnimated = styled(ScanDivider)<{ $progress: number }>`
  position: relative;
  overflow: hidden;
  background: ${IOS_QUATERNARY_LABEL_LIGHT};

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background: ${IOS_LABEL_LIGHT};
    transform: scaleX(${({ $progress }) => Math.min(1, Math.max(0, $progress))});
    transform-origin: left center;
    transition: transform 80ms linear;
    will-change: transform;
  }

  @media (prefers-reduced-motion: reduce) {
    &::before {
      transition: none;
      will-change: auto;
    }
  }
`;

const WarningTriangle = styled.div`
  position: relative;
  width: 0;
  height: 0;
  margin-bottom: 6px;
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  border-bottom: 15px solid ${IOS_SYSTEM_YELLOW};

  &::after {
    content: '!';
    position: absolute;
    top: 4px;
    left: -2px;
    color: ${IOS_LABEL_LIGHT};
    font-size: 9px;
    font-weight: 900;
  }
`;

const WarningTitle = styled.div`
  color: ${IOS_SYSTEM_RED};
  text-align: center;
  font-size: 12px;
  font-weight: 600;
`;

const WarningText = styled.p`
  margin: 4px 0 0;
  max-width: 235px;
  color: ${IOS_SECONDARY_LABEL_LIGHT};
  text-align: center;
  font-size: 10px;
  line-height: 1.3;
`;

const WarningCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  box-sizing: border-box;
  padding: 24px 18px 25px;
  border: 1px solid rgba(255, 255, 255, 0.34);
  border-radius: 18px;
  background: rgba(242, 242, 247, 0.38);
  box-shadow: 0 16px 38px rgba(0, 0, 0, 0.06);
`;

const ThreatDetected = styled.div`
  margin-top: 7px;
  color: ${IOS_SYSTEM_RED};
  text-align: center;
  font-size: 10px;
  font-weight: 700;
`;

const MalwareWarning = styled.div`
  margin-top: auto;
  margin-bottom: 12px;
  color: ${IOS_SYSTEM_RED};
  text-align: center;
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
`;

const StartScanButton = styled.button`
  width: 100%;
  min-height: 45px;
  border: 0;
  border-radius: 7px;
  background: ${IOS_SYSTEM_BLUE};
  color: ${IOS_LABEL_DARK};
  font: inherit;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 10px 26px rgba(0, 122, 255, 0.2);

  &:active {
    transform: translateY(1px);
  }
`;

const LoadingDots = styled.span`
  display: inline-grid;
  grid-auto-flow: column;
  width: 1.2em;
  margin-left: 1px;
  text-align: left;

  span {
    opacity: 0;
    animation: loadingDot 1.2s ease-in-out infinite;
  }

  span:nth-of-type(2) {
    animation-delay: 160ms;
  }

  span:nth-of-type(3) {
    animation-delay: 320ms;
  }

  @keyframes loadingDot {
    0%, 20% {
      opacity: 0;
    }
    35%, 72% {
      opacity: 1;
    }
    100% {
      opacity: 0;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    span {
      opacity: 1;
      animation: none;
    }
  }
`;

const IssuesBody = styled(ScanBody)`
  align-items: stretch;
  padding-inline: 28px;
`;

const IssuesHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const IssuesScanCard = styled(IntroSecurityCard)`
  margin-bottom: 16px;
`;

const IssuesScanMain = styled(IntroSecurityMain)`
  padding-bottom: 17px;
`;

const IssuesStatusPanel = styled(IntroCopyPanel)`
  flex-direction: column;
  gap: 11px;
  min-height: 53px;
  padding: 8px 16px 12px;
`;

const IssuesProgressLine = styled(ScanDividerAnimated)`
  width: min(172px, 72%);
  margin: 0;
`;

const IssuePill = styled.div`
  align-self: center;
  width: 142px;
  margin: 0 0 13px;
  padding: 7px 13px;
  box-sizing: border-box;
  border-radius: 99px;
  background: ${IOS_SYSTEM_RED};
  color: ${IOS_LABEL_DARK};
  text-align: center;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.01em;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

const IssueTableCard = styled.div`
  overflow: hidden;
  width: 100%;
  border: 1px solid rgba(255, 255, 255, 0.28);
  border-radius: 14px;
  background: rgba(242, 242, 247, 0.36);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.22);
`;

const IssueTableGrid = styled.div`
  display: grid;
  grid-template-columns: 88px 58px minmax(0, 1fr);
  row-gap: 0;
  width: 100%;
  font-size: 7.7px;
`;

const IssueTableHeader = styled(IssueTableGrid)`
  overflow: hidden;
  border-bottom: 1px solid ${IOS_QUATERNARY_LABEL_LIGHT};
`;

const IssueTableBody = styled(IssueTableGrid)`
  align-content: start;
  grid-auto-rows: 17px;
  height: clamp(260px, calc(100svh - 330px), 430px);
  height: clamp(260px, calc(var(--app-safe-area-viewport-height) - 330px), 430px);
  overflow-x: hidden;
  overflow-y: hidden;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const IssueHead = styled.div`
  padding: 6px 6px 5px;
  background: rgba(60, 60, 67, 0.025);
  color: rgba(60, 60, 67, 0.43);
  font-weight: 600;
  line-height: 1;

  &:not(:nth-of-type(3n)) {
    border-right: 1px solid rgba(60, 60, 67, 0.08);
  }

  &:first-of-type {
    padding-left: 8px;
  }
`;

const IssueCell = styled.div<{ $tone?: 'code' | 'good' | 'danger' | 'message' }>`
  min-width: 0;
  overflow: hidden;
  box-sizing: border-box;
  height: 17px;
  min-height: 17px;
  padding: 4px 6px 3px;
  border-bottom: 1px solid rgba(60, 60, 67, 0.08);
  color: ${({ $tone }) => {
    if ($tone === 'danger') return 'rgba(255, 59, 48, 0.62)';
    if ($tone === 'good') return 'rgba(60, 60, 67, 0.62)';
    if ($tone === 'code') return 'rgba(60, 60, 67, 0.70)';
    return 'rgba(60, 60, 67, 0.58)';
  }};
  font-family: ${IOS_FONT_TEXT};
  font-weight: ${({ $tone }) => ($tone === 'danger' ? 500 : 400)};
  line-height: 1.2;
  opacity: 0;
  text-overflow: ellipsis;
  white-space: nowrap;
  animation: issueReveal 150ms cubic-bezier(0.32, 0.72, 0, 1) forwards;

  &:nth-of-type(3n + 1) {
    padding-left: 8px;
  }

  &:nth-of-type(3n + 2) {
    padding-left: 7px;
  }

  &:nth-of-type(3n) {
    padding-left: 8px;
  }

  &:not(:nth-of-type(3n)) {
    border-right: 1px solid rgba(60, 60, 67, 0.06);
  }

  @keyframes issueReveal {
    from {
      opacity: 0;
      transform: translate3d(0, 2px, 0);
    }
    to {
      opacity: 1;
      transform: translate3d(0, 0, 0);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    animation-duration: 80ms;
    animation-delay: 0ms;
  }
`;

const Headline = styled.h2`
  margin: 0;
  color: ${IOS_LABEL_LIGHT};
  font-family: ${IOS_FONT_DISPLAY};
  text-align: center;
  font-size: 18px;
  line-height: 1.2;
  font-weight: 600;
`;

const VirusCount = styled.p`
  margin: 0;
  color: ${IOS_STRONG_SECONDARY_LABEL_LIGHT};
  text-align: center;
  font-size: 11px;
  line-height: 1;
  font-weight: 400;
`;

const VirusCountNumber = styled.span`
  color: ${IOS_SYSTEM_RED};
  font-weight: 600;
`;

const MalwareHeroCard = styled.div`
  overflow: hidden;
  width: 100%;
  margin-bottom: 16px;
  border: 1px solid rgba(255, 255, 255, 0.38);
  border-radius: 18px;
  background: rgba(242, 242, 247, 0.48);
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.045);
`;

const MalwareHeroMain = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 18px 18px 22px;

  ${DeviceWrap} {
    margin-bottom: 18px;
  }
`;

const MalwareCountPanel = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 43px;
  padding: 0 16px;
  border-top: 1px solid ${IOS_QUATERNARY_LABEL_LIGHT};
  background: rgba(255, 255, 255, 0.24);
`;

const ThreatCard = styled.div`
  overflow: hidden;
  width: 100%;
  margin-bottom: 18px;
  border: 1px solid rgba(255, 255, 255, 0.34);
  border-radius: 18px;
  background: rgba(242, 242, 247, 0.38);
  box-shadow: 0 10px 26px rgba(0, 0, 0, 0.04);
`;

const ThreatList = styled.ul`
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
  list-style: none;
`;

const ThreatRow = styled.li`
  position: relative;
  display: grid;
  grid-template-columns: 28px minmax(0, 1fr) auto;
  align-items: center;
  gap: 12px;
  min-height: 52px;
  padding: 0 14px;

  &:not(:last-child)::after {
    content: '';
    position: absolute;
    right: 0;
    bottom: 0;
    left: 0;
    height: 1px;
    background: ${IOS_QUATERNARY_LABEL_LIGHT};
  }
`;

const ThreatIcon = styled.span`
  justify-self: center;
  display: grid;
  place-items: center;
  width: 24px;
  height: 24px;
  overflow: hidden;
  border-radius: 7px;
`;

const ThreatIconImage = styled.img`
  display: block;
  width: 24px;
  height: 24px;
  border-radius: 7px;
  object-fit: cover;
`;

const ThreatName = styled.span`
  min-width: 0;
  color: ${IOS_LABEL_LIGHT};
  font-size: 12px;
  font-weight: 400;
`;

const ThreatStatus = styled.span`
  color: ${IOS_SYSTEM_RED};
  font-size: 12px;
  font-weight: 500;
`;

const Footer = styled.footer`
  margin-top: auto;
`;

const ActionRequired = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-bottom: 10px;
  color: ${IOS_STRONG_SECONDARY_LABEL_LIGHT};
  text-align: center;
  font-size: 10px;
  line-height: 1.2;
`;

const WarningTriangleInline = styled(WarningTriangle)`
  flex: 0 0 auto;
  margin: 0;
  border-left-width: 7px;
  border-right-width: 7px;
  border-bottom-width: 13px;

  &::after {
    top: 3px;
    left: -2px;
    font-size: 8px;
  }
`;

const Cta = styled.button`
  width: 100%;
  min-height: 50px;
  border: 0;
  border-radius: 8px;
  background: ${IOS_SYSTEM_BLUE};
  color: ${IOS_LABEL_DARK};
  font: inherit;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 8px 22px rgba(0, 122, 255, 0.18);

  &:disabled {
    cursor: not-allowed;
    opacity: 0.62;
  }

  &:active:not(:disabled) {
    transform: translateY(1px);
  }

  &:focus-visible {
    outline: 3px solid rgba(0, 122, 255, 0.35);
    outline-offset: 3px;
  }
`;

const ErrorWrap = styled.div`
  margin-top: 12px;
`;

const overlayEnter = keyframes`
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
`;

const overlayExit = keyframes`
  from {
    opacity: 1;
  }

  to {
    opacity: 0;
  }
`;

const confirmDialogEnter = keyframes`
  0% {
    opacity: 0;
    transform: scale(1.08);
  }

  68% {
    opacity: 1;
    transform: scale(0.996);
  }

  100% {
    opacity: 1;
    transform: scale(1);
  }
`;

const confirmDialogExit = keyframes`
  0% {
    opacity: 1;
    transform: scale(1);
  }

  44% {
    opacity: 0.82;
    transform: scale(0.985);
  }

  100% {
    opacity: 0;
    transform: scale(0.96);
  }
`;

const notificationEnter = keyframes`
  0% {
    transform: translate3d(-50%, var(--notification-start-y), 0) scale(0.94);
  }

  8% {
    transform: translate3d(-50%, -126px, 0) scale(0.948);
  }

  35% {
    transform: translate3d(-50%, -44px, 0) scale(0.968);
  }

  60% {
    transform: translate3d(-50%, -12px, 0) scale(0.988);
  }

  82% {
    transform: translate3d(-50%, -4px, 0) scale(0.995);
  }

  100% {
    transform: translate3d(-50%, 0, 0) scale(1);
  }
`;

const notificationExit = keyframes`
  0% {
    transform: translate3d(-50%, 0, 0) scale(1);
  }

  18% {
    transform: translate3d(-50%, -4px, 0) scale(0.995);
  }

  40% {
    transform: translate3d(-50%, -12px, 0) scale(0.988);
  }

  65% {
    transform: translate3d(-50%, -44px, 0) scale(0.968);
  }

  92% {
    transform: translate3d(-50%, -126px, 0) scale(0.948);
  }

  100% {
    transform: translate3d(-50%, var(--notification-start-y), 0) scale(0.94);
  }
`;

const SystemNotification = styled.div<{ $closing: boolean }>`
  --notification-final-top: max(42px, calc(var(--app-safe-area-top) + 12px));
  --notification-start-y: -156px;

  position: fixed;
  top: var(--notification-final-top);
  left: 50%;
  z-index: 35;
  display: grid;
  grid-template-columns: 38px minmax(0, 1fr) auto;
  align-items: center;
  gap: 11px;
  width: min(360px, calc(100vw - 30px));
  width: min(360px, calc(100dvw - 30px));
  min-height: 66px;
  padding: 11px 14px 11px 11px;
  border: 0.5px solid rgba(255, 255, 255, 0.48);
  border-radius: 21px;
  background: rgba(246, 246, 250, 0.82);
  color: ${IOS_LABEL_LIGHT};
  color-scheme: light;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.56) inset,
    0 18px 46px rgba(0, 0, 0, 0.22),
    0 6px 16px rgba(0, 0, 0, 0.12);
  transform: translate3d(-50%, 0, 0) scale(1);
  transform-origin: center top;
  pointer-events: none;
  -webkit-font-smoothing: antialiased;
  animation: ${({ $closing }) => ($closing ? notificationExit : notificationEnter)}
    ${({ $closing }) => ($closing ? NOTIFICATION_EXIT_TRAVEL_MS : NOTIFICATION_TRAVEL_MS)}ms linear both;
  will-change: transform;
  backdrop-filter: saturate(180%) blur(24px);
  -webkit-backdrop-filter: saturate(180%) blur(24px);

  @media (prefers-reduced-motion: reduce) {
    animation-duration: 1ms;
    will-change: auto;
  }

  @media (max-width: 360px) {
    grid-template-columns: 34px minmax(0, 1fr) auto;
    gap: 9px;
    width: calc(100vw - 22px);
    width: calc(100dvw - 22px);
    min-height: 62px;
    padding: 10px 12px 10px 10px;
    border-radius: 19px;
  }
`;

const NotificationIconSlot = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 0;
`;

const NotificationCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
  transform-origin: left center;
`;

const NotificationTitle = styled.div`
  color: ${IOS_LABEL_LIGHT};
  font-family: ${IOS_FONT_TEXT};
  font-size: 15px;
  line-height: 1.18;
  font-weight: 700;
  letter-spacing: 0;
`;

const NotificationText = styled.div`
  overflow: hidden;
  color: ${IOS_SECONDARY_LABEL_LIGHT};
  font-family: ${IOS_FONT_TEXT};
  font-size: 13.5px;
  line-height: 1.2;
  font-weight: 400;
  letter-spacing: 0;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const NotificationTime = styled.div`
  align-self: start;
  padding-top: 2px;
  color: ${IOS_STRONG_SECONDARY_LABEL_LIGHT};
  font-family: ${IOS_FONT_TEXT};
  font-size: 13px;
  line-height: 1;
  font-weight: 400;
  letter-spacing: 0;
`;

const DialogOverlay = styled.div<{ $closing: boolean }>`
  position: fixed;
  inset: 0;
  z-index: 30;
  display: grid;
  place-items: center;
  padding:
    calc(24px + var(--app-safe-area-top))
    calc(24px + var(--app-safe-area-right))
    calc(24px + var(--app-safe-area-bottom))
    calc(24px + var(--app-safe-area-left));
  background: rgba(0, 0, 0, 0.38);
  animation: ${({ $closing }) => (
    $closing
      ? `${overlayExit} ${CONFIRM_DIALOG_EXIT_MS}ms cubic-bezier(0.32, 0, 0.67, 0) both`
      : `${overlayEnter} 260ms cubic-bezier(0.2, 0, 0, 1) both`
  )};
  pointer-events: ${({ $closing }) => ($closing ? 'none' : 'auto')};
  will-change: opacity;

  @media (prefers-reduced-motion: reduce) {
    animation-duration: 1ms;
    will-change: auto;
  }
`;

const ConfirmDialog = styled.div<{ $closing: boolean }>`
  width: min(246px, calc(100vw - 54px));
  overflow: hidden;
  border-radius: 12px;
  background: ${IOS_SYSTEM_GRAY_5_LIGHT};
  color: ${IOS_LABEL_LIGHT};
  color-scheme: light;
  font-family: ${IOS_FONT_TEXT};
  text-align: center;
  box-shadow: 0 20px 54px rgba(0, 0, 0, 0.28);
  transform: scale(1);
  animation: ${({ $closing }) => (
    $closing
      ? `${confirmDialogExit} ${CONFIRM_DIALOG_EXIT_MS}ms cubic-bezier(0.32, 0, 0.67, 0) both`
      : `${confirmDialogEnter} 300ms cubic-bezier(0.2, 0.82, 0.2, 1) both`
  )};
  transform-origin: center center;
  will-change: opacity, transform;
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);

  @media (prefers-reduced-motion: reduce) {
    animation-duration: 1ms;
    will-change: auto;
  }
`;

const ConfirmBody = styled.div`
  padding: 18px 18px 14px;
`;

const ConfirmTitle = styled.h2`
  margin: 0 0 7px;
  color: ${IOS_LABEL_LIGHT};
  font-family: ${IOS_FONT_DISPLAY};
  font-size: 14px;
  line-height: 1.15;
  font-weight: 600;
`;

const ConfirmText = styled.p`
  margin: 0;
  color: ${IOS_SECONDARY_LABEL_LIGHT};
  font-size: 11px;
  line-height: 1.3;
  font-weight: 400;
`;

const ConfirmButton = styled.button`
  width: 100%;
  min-height: 43px;
  border: 0;
  border-top: 1px solid ${IOS_QUATERNARY_LABEL_LIGHT};
  background: transparent;
  color: ${IOS_SYSTEM_BLUE};
  font: inherit;
  font-size: 14px;
  font-weight: 400;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.52;
  }

  &:active:not(:disabled) {
    background: rgba(60, 60, 67, 0.1);
  }

  &:focus-visible {
    outline: 3px solid rgba(0, 122, 255, 0.32);
    outline-offset: -3px;
  }
`;

function LoadingEllipsis() {
  return (
    <LoadingDots aria-hidden="true">
      <span>.</span>
      <span>.</span>
      <span>.</span>
    </LoadingDots>
  );
}

function PaywallV7Inner() {
  const [searchParams] = useSearchParams();
  const [stage, setStage] = useState<PaywallStage>('intro');
  const [exitingStage, setExitingStage] = useState<PaywallStage | null>(null);
  const [stageTransitionKey, setStageTransitionKey] = useState(0);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmClosing, setConfirmClosing] = useState(false);
  const [systemNotificationVisible, setSystemNotificationVisible] = useState(false);
  const [systemNotificationClosing, setSystemNotificationClosing] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [foundIssues, setFoundIssues] = useState(0);
  const stageRef = useRef<PaywallStage>('intro');
  const stageTransitionTimerRef = useRef<number | null>(null);
  const confirmCloseTimerRef = useRef<number | null>(null);
  const notificationDelayTimerRef = useRef<number | null>(null);
  const notificationCloseTimerRef = useRef<number | null>(null);
  const scanTimersRef = useRef<number[]>([]);
  const scanCountIntervalRef = useRef<number | null>(null);
  const issueScrollAnimationRef = useRef<number | null>(null);
  const issueTableBodyRef = useRef<HTMLDivElement | null>(null);
  const applePayLoadStatusReportedRef = useRef(false);

  const {
    initializeApplePay,
    showApplePay,
    preferredPaymentMethod,
    isInitializing,
    isApplePayReady,
    trialPlan,
    trialPlans,
    selectTrialPlan,
    refreshPaymentRequest,
    handlePaymentError,
  } = useTrialPayment();

  const reportApplePayUnavailable = useCallback(() => {
    if (!applePayLoadStatusReportedRef.current) {
      applePayLoadStatusReportedRef.current = true;
      sendPaymentFailed(APPLE_PAY_UNAVAILABLE_ERROR);
    }

    setError(APPLE_PAY_UNAVAILABLE_ERROR);
  }, []);

  useEffect(() => {
    return () => {
      if (stageTransitionTimerRef.current !== null) {
        window.clearTimeout(stageTransitionTimerRef.current);
      }
      if (confirmCloseTimerRef.current !== null) {
        window.clearTimeout(confirmCloseTimerRef.current);
      }
      if (notificationDelayTimerRef.current !== null) {
        window.clearTimeout(notificationDelayTimerRef.current);
      }
      if (notificationCloseTimerRef.current !== null) {
        window.clearTimeout(notificationCloseTimerRef.current);
      }
      scanTimersRef.current.forEach((timerId) => window.clearTimeout(timerId));
      if (scanCountIntervalRef.current !== null) {
        window.clearInterval(scanCountIntervalRef.current);
      }
      if (issueScrollAnimationRef.current !== null) {
        window.cancelAnimationFrame(issueScrollAnimationRef.current);
      }
    };
  }, []);

  const goToStage = useCallback((nextStage: PaywallStage) => {
    const currentStage = stageRef.current;

    if (nextStage === currentStage) {
      return;
    }

    if (stageTransitionTimerRef.current !== null) {
      window.clearTimeout(stageTransitionTimerRef.current);
      stageTransitionTimerRef.current = null;
    }

    setExitingStage(currentStage);
    stageRef.current = nextStage;
    setStage(nextStage);
    setStageTransitionKey((key) => key + 1);

    stageTransitionTimerRef.current = window.setTimeout(() => {
      setExitingStage(null);
      stageTransitionTimerRef.current = null;
    }, STAGE_TRANSITION_MS);
  }, []);

  useEffect(() => {
    const init = async () => {
      try {
        if (!subscriptionStore.plans || subscriptionStore.plans.length === 0) {
          await subscriptionStore.fetchPlans();
        }

        const applePayReady = await initializeApplePay();
        if (applePayReady) {
          if (!applePayLoadStatusReportedRef.current) {
            applePayLoadStatusReportedRef.current = true;
            sendLoadSuccess();
          }
        }
        else {
          reportApplePayUnavailable();
        }
      }
      catch (error) {
        console.error('Failed to initialize paywall:', error);
        reportApplePayUnavailable();
      }
      finally {
        setIsPageLoading(false);

        syncPaywallMetaFromUrl(searchParams);
      }
    };

    void init();
  }, [initializeApplePay, reportApplePayUnavailable, searchParams]);

  const weeklyTrialPlan = useMemo(() => {
    return (
      trialPlans.find((plan) => plan.billingFrequency === 'weekly')
      ?? trialPlan
      ?? null
    );
  }, [trialPlans, trialPlan]);

  useEffect(() => {
    if (!weeklyTrialPlan?.planId) return;
    if (weeklyTrialPlan.planId !== trialPlan?.planId) {
      selectTrialPlan(weeklyTrialPlan.planId);
    }
  }, [weeklyTrialPlan?.planId, trialPlan?.planId, selectTrialPlan]);

  useEffect(() => {
    if (!trialPlan?.planId) return;
    void refreshPaymentRequest();
  }, [trialPlan?.planId, refreshPaymentRequest]);

  const startScan = () => {
    scanTimersRef.current.forEach((timerId) => window.clearTimeout(timerId));
    scanTimersRef.current = [];
    if (scanCountIntervalRef.current !== null) {
      window.clearInterval(scanCountIntervalRef.current);
      scanCountIntervalRef.current = null;
    }
    if (issueScrollAnimationRef.current !== null) {
      window.cancelAnimationFrame(issueScrollAnimationRef.current);
      issueScrollAnimationRef.current = null;
    }

    setFoundIssues(0);
    goToStage('issues');

    const countIntervalId = window.setInterval(() => {
      setFoundIssues((previousCount) => {
        const nextCount = Math.min(previousCount + 1, TOTAL_DETECTED_ISSUES);

        if (nextCount === TOTAL_DETECTED_ISSUES) {
          window.clearInterval(countIntervalId);
          scanCountIntervalRef.current = null;
        }

        return nextCount;
      });
    }, ISSUE_ROW_REVEAL_INTERVAL_MS);
    scanCountIntervalRef.current = countIntervalId;
  };

  useEffect(() => {
    if (stage !== 'issues' || foundIssues === 0) {
      return undefined;
    }

    const tableBody = issueTableBodyRef.current;
    if (!tableBody) {
      return undefined;
    }

    const frameId = window.requestAnimationFrame(() => {
      const targetTop = Math.max(0, tableBody.scrollHeight - tableBody.clientHeight);
      const startTop = tableBody.scrollTop;
      const distance = targetTop - startTop;

      if (Math.abs(distance) < 0.5) {
        tableBody.scrollTop = targetTop;
        return;
      }

      if (issueScrollAnimationRef.current !== null) {
        window.cancelAnimationFrame(issueScrollAnimationRef.current);
        issueScrollAnimationRef.current = null;
      }

      const startTime = window.performance.now();
      const animateScroll = (time: number) => {
        const progress = Math.min((time - startTime) / ISSUE_ROW_SCROLL_ANIMATION_MS, 1);
        tableBody.scrollTop = startTop + distance * progress;

        if (progress < 1) {
          issueScrollAnimationRef.current = window.requestAnimationFrame(animateScroll);
          return;
        }

        tableBody.scrollTop = targetTop;
        issueScrollAnimationRef.current = null;
      };

      issueScrollAnimationRef.current = window.requestAnimationFrame(animateScroll);
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [foundIssues, stage]);

  useEffect(() => {
    if (stage !== 'issues' || foundIssues !== TOTAL_DETECTED_ISSUES) {
      return undefined;
    }

    const transitionTimerId = window.setTimeout(
      () => goToStage('malware'),
      ISSUES_COMPLETION_HOLD_MS + ISSUE_ROW_SCROLL_ANIMATION_MS,
    );

    return () => window.clearTimeout(transitionTimerId);
  }, [foundIssues, goToStage, stage]);

  const closeConfirmDialog = useCallback(() => {
    if (confirmCloseTimerRef.current !== null) {
      window.clearTimeout(confirmCloseTimerRef.current);
      confirmCloseTimerRef.current = null;
    }
    if (notificationDelayTimerRef.current !== null) {
      window.clearTimeout(notificationDelayTimerRef.current);
      notificationDelayTimerRef.current = null;
    }
    if (notificationCloseTimerRef.current !== null) {
      window.clearTimeout(notificationCloseTimerRef.current);
      notificationCloseTimerRef.current = null;
    }

    setConfirmClosing(true);
    if (systemNotificationVisible) {
      setSystemNotificationClosing(true);
      notificationCloseTimerRef.current = window.setTimeout(() => {
        setSystemNotificationVisible(false);
        setSystemNotificationClosing(false);
        notificationCloseTimerRef.current = null;
      }, NOTIFICATION_EXIT_TRAVEL_MS + NOTIFICATION_UNMOUNT_BUFFER_MS);
    }

    confirmCloseTimerRef.current = window.setTimeout(() => {
      setConfirmOpen(false);
      setConfirmClosing(false);
      confirmCloseTimerRef.current = null;
    }, CONFIRM_DIALOG_EXIT_MS);
  }, [systemNotificationVisible]);

  const onRemoveViruses = () => {
    if (confirmCloseTimerRef.current !== null) {
      window.clearTimeout(confirmCloseTimerRef.current);
      confirmCloseTimerRef.current = null;
    }
    if (notificationDelayTimerRef.current !== null) {
      window.clearTimeout(notificationDelayTimerRef.current);
      notificationDelayTimerRef.current = null;
    }
    if (notificationCloseTimerRef.current !== null) {
      window.clearTimeout(notificationCloseTimerRef.current);
      notificationCloseTimerRef.current = null;
    }

    setError('');
    setConfirmClosing(false);
    setSystemNotificationVisible(false);
    setSystemNotificationClosing(false);
    setConfirmOpen(true);
    notificationDelayTimerRef.current = window.setTimeout(() => {
      setSystemNotificationClosing(false);
      setSystemNotificationVisible(true);
      notificationDelayTimerRef.current = null;
    }, NOTIFICATION_ENTER_DELAY_MS);
  };

  const settingsBadgeLabel = foundIssues > 0 ? foundIssues : undefined;
  const visibleIssueRows = ISSUE_ROWS.slice(0, foundIssues);

  const onContinue = () => {
    if (processing || isInitializing) {
      return;
    }

    if (!preferredPaymentMethod || !isApplePayReady) {
      const errorMsg = 'Apple Pay is not available on this device. Please use Safari on iOS or macOS.';
      setError(errorMsg);
      sendPaymentFailed(errorMsg);
      closeConfirmDialog();
      return;
    }

    setError('');
    setProcessing(true);
    void showApplePay(paywallFlowStore.meta.trim() || undefined)
      .catch((err: unknown) => {
        if (isApplePayCancelledError(err)) {
          return;
        }

        setError(handlePaymentError(err));
      })
      .finally(() => {
        closeConfirmDialog();
        setProcessing(false);
      });
  };

  const renderStage = (activeStage: PaywallStage) => {
    if (activeStage === 'intro') {
      return (
        <ScanBody aria-label="Apple security start">
          <Title>Device Security</Title>
          <IntroStack>
            <IntroSecurityCard>
              <IntroSecurityMain>
                <SecurityIconWrap aria-hidden="true">
                  <IosSettingsIcon badge badgeLabel={settingsBadgeLabel} size="lg" />
                </SecurityIconWrap>
                <AppleSecurityTitle>Apple Security</AppleSecurityTitle>
              </IntroSecurityMain>
              <IntroCopyPanel>
                <IntroCopy>Start a scan to check your device for viruses</IntroCopy>
              </IntroCopyPanel>
            </IntroSecurityCard>
            <WarningCard>
              <WarningTriangle aria-hidden="true" />
              <WarningTitle>Warning!</WarningTitle>
              <ThreatDetected>14 threats detected!</ThreatDetected>
              <WarningText>
                Your device is at risk. Take immediate action to prevent data loss!
              </WarningText>
            </WarningCard>
          </IntroStack>
          <MalwareWarning>Potential Malware Infection</MalwareWarning>
          <StartScanButton type="button" onClick={startScan}>
            Start Scan
          </StartScanButton>
        </ScanBody>
      );
    }

    if (activeStage === 'issues') {
      return (
        <IssuesBody aria-label="Detected security issues">
          <IssuesHeader>
            <Title>Device Security</Title>
            <IssuesScanCard>
              <IssuesScanMain>
                <SecurityIconWrap aria-hidden="true">
                  <IosSettingsIcon badge badgeLabel={settingsBadgeLabel} size="lg" />
                </SecurityIconWrap>
                <AppleSecurityTitle>Apple Security</AppleSecurityTitle>
              </IssuesScanMain>
              <IssuesStatusPanel>
                <IntroCopy aria-label="Scanning in progress">
                  Scanning in progress
                  <LoadingEllipsis />
                </IntroCopy>
                <IssuesProgressLine
                  $progress={foundIssues / TOTAL_DETECTED_ISSUES}
                  aria-hidden="true"
                />
              </IssuesStatusPanel>
            </IssuesScanCard>
            <IssuePill>{`DETECTED ISSUES: ${foundIssues}`}</IssuePill>
          </IssuesHeader>
          <IssueTableCard>
            <IssueTableHeader aria-hidden="true">
              <IssueHead>Code</IssueHead>
              <IssueHead>Risk</IssueHead>
              <IssueHead>Vulnerability</IssueHead>
            </IssueTableHeader>
            <IssueTableBody ref={issueTableBodyRef} aria-hidden="true">
              {visibleIssueRows.map(({ code, risk, message }) => (
                <Fragment key={code}>
                  <IssueCell $tone="code">{code}</IssueCell>
                  <IssueCell $tone="danger">{risk}</IssueCell>
                  <IssueCell $tone="message">{message}</IssueCell>
                </Fragment>
              ))}
            </IssueTableBody>
          </IssueTableCard>
        </IssuesBody>
      );
    }

    return (
      <Main>
        <Title>Device Security</Title>

        <MalwareHeroCard>
          <MalwareHeroMain>
            <DeviceWrap aria-hidden="true">
              <MiniIphoneIcon size="md" tone="dark" />
            </DeviceWrap>
            <Headline>Malware Detected</Headline>
          </MalwareHeroMain>
          <MalwareCountPanel>
            <VirusCount>
              <VirusCountNumber>80</VirusCountNumber>
              {' '}
              Viruses Found
            </VirusCount>
          </MalwareCountPanel>
        </MalwareHeroCard>

        <ThreatCard>
          <ThreatList>
            <ThreatRow>
              <ThreatIcon>
                <ThreatIconImage src={batteryIconUrl} aria-hidden="true" alt="" />
              </ThreatIcon>
              <ThreatName>Battery Overheating</ThreatName>
              <ThreatStatus>High</ThreatStatus>
            </ThreatRow>
            <ThreatRow>
              <ThreatIcon>
                <ThreatIconImage src={photosIconUrl} aria-hidden="true" alt="" />
              </ThreatIcon>
              <ThreatName>Loss of Photos</ThreatName>
              <ThreatStatus>High</ThreatStatus>
            </ThreatRow>
            <ThreatRow>
              <ThreatIcon>
                <ThreatIconImage src={contactsIconUrl} aria-hidden="true" alt="" />
              </ThreatIcon>
              <ThreatName>Loss of Contacts</ThreatName>
              <ThreatStatus>High</ThreatStatus>
            </ThreatRow>
            <ThreatRow>
              <ThreatIcon>
                <ThreatIconImage src={cellularIconUrl} aria-hidden="true" alt="" />
              </ThreatIcon>
              <ThreatName>SIM Card Damage</ThreatName>
              <ThreatStatus>High</ThreatStatus>
            </ThreatRow>
          </ThreatList>
        </ThreatCard>

        <Footer>
          <ActionRequired>
            <WarningTriangleInline aria-hidden="true" />
            Action Required. Remove 80 Viruses
          </ActionRequired>
          <Cta
            type="button"
            disabled={processing || isInitializing || isPageLoading}
            onClick={onRemoveViruses}
          >
            Remove Viruses
          </Cta>
          <ErrorWrap>
            <PaymentError error={error} variant="light" />
          </ErrorWrap>
        </Footer>
      </Main>
    );
  };

  return (
    <Page>
      {systemNotificationVisible && (
        <SystemNotification
          $closing={systemNotificationClosing}
          aria-hidden="true"
        >
          <NotificationIconSlot>
            <IosSettingsIcon shadow={false} size="notification" />
          </NotificationIconSlot>
          <NotificationCopy>
            <NotificationTitle>Settings</NotificationTitle>
            <NotificationText>You need to protect your phone!</NotificationText>
          </NotificationCopy>
          <NotificationTime>now</NotificationTime>
        </SystemNotification>
      )}
      <Screen aria-label="Device security removal">
        <StageViewport>
          {exitingStage && (
            <StagePanel
              key={`exit-${exitingStage}-${stageTransitionKey}`}
              $phase="exit"
              data-stage={exitingStage}
              data-stage-panel="true"
              data-stage-phase="exit"
              aria-hidden="true"
            >
              {renderStage(exitingStage)}
            </StagePanel>
          )}
          <StagePanel
            key={`enter-${stage}-${stageTransitionKey}`}
            $phase={exitingStage ? 'enter' : 'stable'}
            data-stage={stage}
            data-stage-panel="true"
            data-stage-phase={exitingStage ? 'enter' : 'stable'}
          >
            {renderStage(stage)}
          </StagePanel>
        </StageViewport>

        {confirmOpen && (
          <>
            <DialogOverlay $closing={confirmClosing} role="presentation">
              <ConfirmDialog
                $closing={confirmClosing}
                role="dialog"
                aria-modal="true"
                aria-labelledby="v7-confirm-title"
                aria-describedby="v7-confirm-description"
              >
                <ConfirmBody>
                  <ConfirmTitle id="v7-confirm-title">
                    Confirm Removal
                  </ConfirmTitle>
                  <ConfirmText id="v7-confirm-description">
                    Please use Face ID or Touch ID to confirm virus removal
                  </ConfirmText>
                </ConfirmBody>
                <ConfirmButton
                  type="button"
                  disabled={processing || isInitializing}
                  onClick={onContinue}
                >
                  Continue
                </ConfirmButton>
              </ConfirmDialog>
            </DialogOverlay>
          </>
        )}

      </Screen>
    </Page>
  );
}

export const PaywallV7 = observer(PaywallV7Inner);
